import { useLocation, useSearchParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import styled from '@emotion/styled/macro';
import { useFeatureFlagEnabled } from '@sunrun/sales-experience-shared/react-feature-flags';
import {
  DEFAULT_LV_CONTROLS,
  DEFAULT_PIPELINE_CONTROLS,
  PipelineHeader,
} from 'components/organisms/PipelineHeader';
import { PipelineTab } from 'components/organisms/PipelineTab';
import { usePipeline, useListViewData } from 'providers/Pipeline';
import { ListViewTab } from 'components/organisms/ListViewTab';
import { useUserProfile } from 'providers/UserProfile';
import { ListViewControls, PipelineControls } from 'types/prospect';
import { SplatIFrame } from 'components/organisms/SplatIFrame';
import { FEATURE_FLAGS } from 'constants/FeatureFlags';
import {
  PIPELINE_DRAWER_HOST_ID,
  SalePhase,
} from 'components/atoms/PipelineConsts';
import { setAppliedPipelineSalesReps } from 'helpers/pipelineSalesRepsStorage';

const Pipeline = () => {
  const [searchParams] = useSearchParams();
  const { pathname } = useLocation();
  const newListViewOnPipelineEnabled = useFeatureFlagEnabled(
    FEATURE_FLAGS.NEW_LIST_VIEW_ON_PIPELINE,
  );
  const [showSearchListView, setShowSearchListView] = useState(false);
  const isListViewRoute = pathname.includes('listview');
  const filterUrgent = searchParams.get('filterUrgent');
  const [currentView, setCurrentView] = useState<'pipeline' | 'list'>(
    isListViewRoute ? 'list' : 'pipeline',
  );

  const [pipelineControls, setPipelineControls] = useState<PipelineControls>({
    ...DEFAULT_PIPELINE_CONTROLS,
    includeUrgent: filterUrgent === 'true',
  });
  const [listViewControls, setListViewControls] = useState<ListViewControls>({
    ...DEFAULT_LV_CONTROLS,
  });
  const [salePhase, setSalePhase] = useState<SalePhase>('all');

  useEffect(() => {
    setPipelineControls((current) => ({
      ...current,
      includeUrgent: filterUrgent === 'true',
    }));
  }, [filterUrgent]);

  useEffect(() => {
    if (pipelineControls.salesReps.length > 0) {
      setAppliedPipelineSalesReps(pipelineControls.salesReps);
    }
  }, [pipelineControls.salesReps]);

  const { splatUserType } = useUserProfile();

  const {
    data: pipelineData,
    isLoading: isPipelineLoading,
    error: pipelineError,
    isValidating: isPipelineRefreshing,
    refresh: refreshPipeline,
  } = usePipeline(splatUserType, pipelineControls.salesReps);

  const {
    isLoading: isListViewDataLoading,
    isTruncated: isListViewDataTruncated,
  } = useListViewData(
    listViewControls.beginCreatedDate,
    listViewControls.endCreatedDate,
  );

  return (
    <PipelinePage>
      <PipelineHeader
        appliedPipelineControls={pipelineControls}
        appliedListViewControls={listViewControls}
        onApplyPipelineControl={setPipelineControls}
        onApplyListViewControl={setListViewControls}
        onViewChange={setCurrentView}
        view={currentView}
        isListViewRoute={isListViewRoute}
        showSearchListView={showSearchListView}
        setShowSearchListView={setShowSearchListView}
        isListViewDataLoading={isListViewDataLoading}
        isListViewDataTruncated={isListViewDataTruncated}
        salePhase={salePhase}
        onSalePhaseChange={setSalePhase}
      />
      {!isListViewRoute && (
        <PipelineTab
          controls={pipelineControls}
          setControls={setPipelineControls}
          pipelineData={pipelineData ?? undefined}
          isPipelineLoading={isPipelineLoading}
          visible={currentView === 'pipeline'}
          pipelineError={pipelineError}
          setCurrentView={setCurrentView}
          onRefresh={refreshPipeline}
          isRefreshing={isPipelineRefreshing}
          salePhase={salePhase}
        />
      )}
      {newListViewOnPipelineEnabled && (
        <ListViewTabWrapper>
          <ListViewTab
            clearSearchFilters={() => {
              setListViewControls((prev) => ({
                ...prev,
                searchTerm: '',
              }));
              setShowSearchListView(false);
            }}
            visible={currentView === 'list'}
            setCurrentView={setCurrentView}
            listViewControls={listViewControls}
            isListViewRoute={isListViewRoute}
            onSortChange={({ sortParam, sortDirection }) =>
              setListViewControls((prev) => ({
                ...prev,
                sortParam,
                sortDirection,
              }))
            }
          />
        </ListViewTabWrapper>
      )}
      {!newListViewOnPipelineEnabled && (
        <SplatIFrame visible={currentView === 'list'} page={'customers'} />
      )}
      <DrawerHost id={PIPELINE_DRAWER_HOST_ID} />
    </PipelinePage>
  );
};

const PipelinePage = styled.div`
  height: 100vh;
  display: grid;
  grid-template-rows: auto 1fr;
  min-width: fit-content;
  position: relative;
`;

const DrawerHost = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 100%;
  max-width: 100%;
  overflow: hidden;
  transform: translateZ(0);
  pointer-events: none;
  z-index: 200;

  .sr-sliding-drawer-backdrop,
  .sr-sliding-drawer {
    pointer-events: auto;
  }
`;

const ListViewTabWrapper = styled.div`
  min-width: 0;
  max-width: 100%;
  overflow-x: hidden;
  height: 100%;
`;

export { Pipeline };
